import { Flex, Heading, Icon, Text } from "@chakra-ui/react";
import React from "react";
import { HiOutlineLocationMarker } from "react-icons/hi";
import { HiOutlinePhone, HiOutlineVideoCamera } from "react-icons/hi2";

import { RouterLink } from "../../../../components";
import { InterviewsListInterviewers } from "../../../components/InterviewsList";
import {
  CallListItemFragment,
  ScheduledInterviewListItemFragment,
} from "../../../graphql";

type InterviewInterviewerSectionProps = {
  shouldHideInterviewType?: boolean;
  onClickInterview?(): void;
} & (
  | {
      scheduledInterview: ScheduledInterviewListItemFragment & {
        callId?: string | null;
      };
      call?: never;
    }
  | {
      scheduledInterview?: never;
      call: Pick<CallListItemFragment, "id" | "name" | "type" | "interviewers">;
    }
);

const InterviewInterviewerSection: React.FC<
  InterviewInterviewerSectionProps
> = ({
  scheduledInterview,
  call,
  shouldHideInterviewType = false,
  onClickInterview,
}) => {
  let interviewers;
  if (scheduledInterview) {
    interviewers = scheduledInterview?.scheduledInterviewers?.map(
      (i) => i.user
    );
  } else {
    interviewers = call.interviewers;
  }
  let iconToDisplay;
  let interviewTypeName = "";
  const interviewType = (
    scheduledInterview?.interviewType?.toString() ||
    call?.type.toString() ||
    ""
  ).toLowerCase();
  if (interviewType.includes("zoom")) {
    interviewTypeName = "Zoom";
    iconToDisplay = HiOutlineVideoCamera;
  } else if (interviewType.includes("google")) {
    interviewTypeName = "Google Meet";
    iconToDisplay = HiOutlineVideoCamera;
  } else if (interviewType.includes("person")) {
    interviewTypeName = "In-person";
    iconToDisplay = HiOutlineLocationMarker;
  } else if (interviewType.includes("teams")) {
    interviewTypeName = "Teams";
    iconToDisplay = HiOutlineVideoCamera;
  } else if (
    interviewType.includes("phone") ||
    interviewType.includes("browser") ||
    interviewType.includes("incoming") ||
    interviewType.includes("outgoing")
  ) {
    interviewTypeName = "Phone";
    iconToDisplay = HiOutlinePhone;
  }

  const renderScheduledInterview = (): JSX.Element | null => {
    if (!scheduledInterview) {
      return null;
    }

    // When rendering from the 'Scheduled Interviews' page,
    // the `scheduledInterview.callId` property refers to the call
    // with the longest duration.
    const scheduledInterviewCallId = scheduledInterview?.callId;
    const interviewName = scheduledInterview.name || "Untitled";

    // Define the Heading component once to avoid duplication
    const headingElement = (
      <Heading
        as="h6"
        size="xs"
        position="relative"
        wordBreak="break-word"
        color="gray.800"
        lineHeight="6"
        fontWeight="inherit"
        textOverflow="ellipsis"
        overflow="hidden"
        whiteSpace="nowrap"
      >
        {interviewName}
      </Heading>
    );

    // If there's a callId, add a link to the interview
    if (scheduledInterviewCallId) {
      return (
        <RouterLink
          to={`/interview/${scheduledInterviewCallId}`}
          color="gray.800"
          fontWeight="inherit"
          _hover={{ textDecoration: "underline", color: "inherit" }}
          onClick={onClickInterview}
        >
          {headingElement}
        </RouterLink>
      );
    }

    return headingElement;
  };

  return (
    <Flex direction="column" overflow="hidden" fontWeight="semibold">
      {renderScheduledInterview()}
      {call && (
        <RouterLink
          to={`/interview/${call.id}`}
          color="gray.800"
          fontWeight="inherit"
          _hover={{ textDecoration: "underline", color: "inherit" }}
          onClick={onClickInterview}
        >
          <Heading
            as="h6"
            size="xs"
            wordBreak="break-word"
            position="relative"
            lineHeight="6"
            textOverflow="ellipsis"
            fontWeight="inherit"
            overflow="hidden"
            whiteSpace="nowrap"
          >
            {call.name || "Untitled"}
          </Heading>
        </RouterLink>
      )}

      <InterviewsListInterviewers
        interviewers={interviewers}
        size="xs"
        ml="0"
        marginTop="1.5"
      />

      <Flex mt="1.5" alignItems="center">
        {!shouldHideInterviewType && interviewTypeName && (
          <>
            <Icon as={iconToDisplay} color="gray.500" boxSize="4" />
            <Text
              fontSize="sm"
              color="gray.500"
              ml="1.5"
              fontWeight="500"
              lineHeight="20px"
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
            >
              {interviewTypeName}
            </Text>
          </>
        )}
      </Flex>
    </Flex>
  );
};

export default InterviewInterviewerSection;
